import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { BookMarked, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSeriesTracking } from '@/hooks/useSeriesTracking';

interface AuthorSeriesGroup {
  series_name: string;
  books: any[];
  book_count: number;
  first_published: number;
}

interface AuthorSeriesProgressProps {
  authorName: string;
  seriesGroups: AuthorSeriesGroup[];
}

export function AuthorSeriesProgress({ authorName, seriesGroups }: AuthorSeriesProgressProps) {
  const { allSeries } = useSeriesTracking();

  if (seriesGroups.length === 0) {
    return null;
  }

  // Find tracked series for this author
  const trackedSeriesMap = new Map(
    allSeries
      .filter(s => s.author_name === authorName && !s.is_dismissed)
      .map(s => [s.series_name, s])
  );

  const seriesWithTracking = seriesGroups
    .map(group => ({
      ...group,
      tracking: trackedSeriesMap.get(group.series_name.toLowerCase().trim()),
    }))
    .filter(group => group.tracking); // Only show tracked series

  if (seriesWithTracking.length === 0) {
    return null;
  }

  return (
    <Card className="card-elevated mb-6">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <BookMarked className="h-5 w-5 text-primary" />
            Your Series Progress
          </h3>
          <Button variant="ghost" size="sm" asChild>
            <Link to="/series-tracker" className="flex items-center gap-1">
              View All
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>
        </div>

        <div className="space-y-4">
          {seriesWithTracking.map(({ series_name, books, tracking }) => {
            if (!tracking) return null;

            const progressPercent = tracking.total_books_known
              ? Math.round((tracking.highest_book_position / tracking.total_books_known) * 100)
              : 0;

            const isComplete = tracking.total_books_known
              ? tracking.highest_book_position >= tracking.total_books_known
              : false;

            return (
              <div
                key={series_name}
                className="p-4 rounded-lg bg-muted/30 border border-border/50 space-y-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium line-clamp-1">{series_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {books.length} {books.length === 1 ? 'book' : 'books'} in your library
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {isComplete && (
                      <Badge variant="default" className="shrink-0">
                        Complete
                      </Badge>
                    )}
                    <span className="text-sm font-medium whitespace-nowrap">
                      Book {tracking.highest_book_position}
                      {tracking.total_books_known ? ` / ${tracking.total_books_known}` : ''}
                    </span>
                  </div>
                </div>
                {tracking.total_books_known && (
                  <Progress value={progressPercent} className="h-2" />
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
